/*
 * sv-newsletter.js
 * ----------------
 * Client-side handler for [data-sv-klaviyo-form] newsletter capture forms.
 * POSTs directly to Klaviyo's Client Subscriptions API (browser-safe; uses
 * the public company_id only — no private key ever touches the client).
 *
 * Endpoint:
 *   POST https://a.klaviyo.com/client/subscriptions/?company_id=<publicKey>
 *   Headers: Content-Type: application/vnd.api+json, revision: 2026-07-15
 *
 * Anti-bot:
 *   · Honeypot field name="website" — bots fill it, humans don't. Silent
 *     fake-success if non-empty (don't tip off the bot).
 *   · In-flight lock prevents duplicate requests; fast human submissions remain valid.
 *   · TODO(Wave 6c or later): Cloudflare Turnstile — render an invisible
 *     widget per form, require a fresh token, include it in the POST body
 *     as a custom profile property (or server-side verify via a proxy).
 *
 * Config:
 *   window.SV_KLAVIYO = { publicKey: '...', listId: '...' }
 *   Injected by layout/theme.liquid from theme settings.
 *
 * Source tagging:
 *   Each form carries data-sv-klaviyo-source="<tag>". Tag is sent to
 *   Klaviyo as profile property $source so segments/reports can tell
 *   which capture surface the subscriber came from.
 */
(function () {
  'use strict';

  var CONFIG = window.SV_KLAVIYO || {};
  var ENDPOINT_BASE = 'https://a.klaviyo.com/client/subscriptions/';
  var REVISION = '2026-07-15';

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function show(el) { if (el) { el.removeAttribute('hidden'); } }
  function hide(el) { if (el) { el.setAttribute('hidden', ''); } }

  /**
   * Build the Klaviyo JSON:API subscription payload.
   * Shape per Klaviyo Client Subscriptions API (revision 2026-07-15).
   */
  function buildPayload(email, listId, source, extraProps) {
    var properties = { $source: source || 'unknown' };
    if (extraProps) {
      Object.keys(extraProps).forEach(function (k) {
        if (extraProps[k]) { properties[k] = extraProps[k]; }
      });
    }
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'unknown',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
  }

  function postToKlaviyo(publicKey, payload) {
    var url = ENDPOINT_BASE + '?company_id=' + encodeURIComponent(publicKey);
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'revision': REVISION
      },
      body: JSON.stringify(payload)
    });
  }

  function wireForm(form) {
    // Record initialization time; fast human submissions remain valid.
    var renderedAt = Date.now();
    var submitting = false;
    form.setAttribute('data-sv-klaviyo-rendered-at', String(renderedAt));

    // Locate adjacent success/error DOM (scoped to the form's wrapper).
    var scope = form.closest('[data-sv-newsletter]') || form.parentElement || document;
    var successEl = scope.querySelector('[data-sv-klaviyo-success]');
    var errorEl = scope.querySelector('[data-sv-klaviyo-error]');

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // Hide any prior state messages.
      hide(successEl);
      hide(errorEl);

      // Honeypot — bots fill name="website", humans don't.
      var honey = form.querySelector('input[name="website"]');
      if (honey && honey.value && honey.value.trim() !== '') {
        // Fake-success so the bot doesn't learn it was caught.
        form.reset();
        show(successEl);
        return;
      }

      // Autofill and password managers can submit instantly. Never report
      // success without a subscription request accepted by Klaviyo.
      if (submitting) return;
      if (!form.reportValidity()) return;

      // Unconfigured guard — if theme settings aren't set, surface the
      // existing static error message (already rendered by Liquid) and
      // bail before hitting the network.
      if (form.hasAttribute('data-sv-klaviyo-unconfigured')) {
        show(errorEl);
        return;
      }

      if (!CONFIG.publicKey || !CONFIG.listId) {
        show(errorEl);
        return;
      }

      var emailInput = form.querySelector('input[name="email"]');
      var email = emailInput ? (emailInput.value || '').trim() : '';
      if (!email) {
        show(errorEl);
        return;
      }

      var source = form.getAttribute('data-sv-klaviyo-source') || 'unknown';
      var zoneInput = form.querySelector('[name="growing_zone"]');
      var zone = zoneInput ? zoneInput.value : '';

      var payload = buildPayload(email, CONFIG.listId, source, {
        growing_zone: zone
      });

      // Disable submit while in-flight to prevent double-fire.
      var submitBtn = form.querySelector('button[type="submit"]');
      var originalLabel = submitBtn ? submitBtn.innerHTML : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }

      submitting = true;
      postToKlaviyo(CONFIG.publicKey, payload)
        .then(function (res) {
          // Klaviyo returns 202 Accepted on success. Any 2xx is good.
          if (res.ok || res.status === 202) {
            form.reset();
            show(successEl);
          } else {
            return res.text().then(function (body) {
              // eslint-disable-next-line no-console
              console.warn('[sv-newsletter] Subscription request failed', res.status);
              show(errorEl);
            });
          }
        })
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.warn('[sv-newsletter] Subscription request could not complete');
          show(errorEl);
        })
        .then(function () {
          submitting = false;
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.removeAttribute('aria-busy');
            submitBtn.innerHTML = originalLabel;
          }
        });
    });

    // The shared form starts disabled in HTML. Enable it only after this
    // handler is attached and the existing newsletter configuration exists.
    // This also keeps a missing configuration from looking ready to submit.
    var readyButton = form.querySelector('button[type="submit"]');
    if (readyButton) {
      readyButton.disabled = form.hasAttribute('data-sv-klaviyo-unconfigured') || !CONFIG.publicKey || !CONFIG.listId;
    }
  }

  ready(function () {
    var forms = document.querySelectorAll('[data-sv-klaviyo-form]');
    Array.prototype.forEach.call(forms, wireForm);
  });
})();
